export const JSONRPC_VERSION = '2.0' as const;

export const ERROR_CODES = {
  PARSE_ERROR: -32700,
  INVALID_REQUEST: -32600,
  METHOD_NOT_FOUND: -32601,
  INVALID_PARAMS: -32602,
  INTERNAL_ERROR: -32603,
  TRANSPORT_CLOSED: -32010,
  PERMANENT_FAILURE: -32011,
} as const;

export type RpcId = string | number | null;

export interface RpcRequest<P = unknown> {
  jsonrpc: '2.0';
  id?: RpcId;
  method: string;
  params?: P;
}

export interface RpcSuccessResponse<R = unknown> {
  jsonrpc: '2.0';
  id: RpcId;
  result: R;
}

export interface RpcErrorResponse {
  jsonrpc: '2.0';
  id: RpcId;
  error: { code: number; message: string; data?: unknown };
}

export type RpcResponse<R = unknown> = RpcSuccessResponse<R> | RpcErrorResponse;

export type IncomingHandler = (req: RpcRequest) => Promise<RpcResponse | void> | RpcResponse | void;
export type NotificationHandler = (method: string, params: unknown) => void;
export type StatusHandler = (status: WsStatus) => void;

export type WsStatus =
  | { kind: 'connecting' }
  | { kind: 'open' }
  | { kind: 'closed'; code: number; reason: string }
  | { kind: 'reconnecting'; attempt: number; delay_ms: number }
  | { kind: 'permanent_failure'; reason: string };

export interface WsClientOptions {
  maxAttempts?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  webSocketCtor?: typeof WebSocket;
  now?: () => number;
}

interface PendingCall {
  resolve: (resp: RpcResponse) => void;
  reject: (err: Error) => void;
}

export class WsClient {
  private socket: WebSocket | null = null;
  private url: string | null = null;
  private getJwt: (() => string | Promise<string>) | null = null;
  private nextId = 1;
  private readonly pending = new Map<string, PendingCall>();
  private incoming: IncomingHandler | null = null;
  private notifications: NotificationHandler | null = null;
  private statusListener: StatusHandler | null = null;
  private attempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private explicitlyClosed = false;
  private readonly maxAttempts: number;
  private readonly baseDelayMs: number;
  private readonly maxDelayMs: number;
  private readonly WSCtor: typeof WebSocket;

  constructor(opts: WsClientOptions = {}) {
    this.maxAttempts = opts.maxAttempts ?? 10;
    this.baseDelayMs = opts.baseDelayMs ?? 1000;
    this.maxDelayMs = opts.maxDelayMs ?? 30000;
    const ctor = opts.webSocketCtor ?? (typeof WebSocket !== 'undefined' ? WebSocket : undefined);
    if (!ctor) {
      throw new Error('WsClient: no WebSocket implementation available');
    }
    this.WSCtor = ctor;
  }

  connect(url: string, getJwt: () => string | Promise<string>): void {
    this.url = url;
    this.getJwt = getJwt;
    this.explicitlyClosed = false;
    this.attempts = 0;
    this.openSocket();
  }

  close(): void {
    this.explicitlyClosed = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      try {
        this.socket.close(1000, 'client_close');
      } catch {
        // ignore
      }
      this.socket = null;
    }
    for (const [, pending] of this.pending) {
      pending.reject(new Error('connection closed'));
    }
    this.pending.clear();
  }

  send<R = unknown>(req: Omit<RpcRequest, 'jsonrpc' | 'id'>): Promise<RpcResponse<R>> {
    const id = String(this.nextId++);
    const envelope: RpcRequest = {
      jsonrpc: JSONRPC_VERSION,
      id,
      method: req.method,
      params: req.params,
    };
    return new Promise<RpcResponse<R>>((resolve, reject) => {
      if (!this.socket || this.socket.readyState !== this.WSCtor.OPEN) {
        reject(new Error('socket not open'));
        return;
      }
      this.pending.set(id, {
        resolve: (resp) => resolve(resp as RpcResponse<R>),
        reject,
      });
      try {
        this.socket.send(JSON.stringify(envelope));
      } catch (err) {
        this.pending.delete(id);
        reject(err instanceof Error ? err : new Error('send failed'));
      }
    });
  }

  notify(method: string, params?: unknown): void {
    const envelope: RpcRequest = { jsonrpc: JSONRPC_VERSION, method, params };
    if (!this.socket || this.socket.readyState !== this.WSCtor.OPEN) {
      throw new Error('socket not open');
    }
    this.socket.send(JSON.stringify(envelope));
  }

  onIncoming(handler: IncomingHandler): void {
    this.incoming = handler;
  }

  onNotification(handler: NotificationHandler): void {
    this.notifications = handler;
  }

  onStatus(handler: StatusHandler): void {
    this.statusListener = handler;
  }

  private openSocket(): void {
    if (!this.url || !this.getJwt) return;
    this.emitStatus({ kind: 'connecting' });
    Promise.resolve(this.getJwt())
      .then(jwt => this.createSocket(jwt))
      .catch(() => { if (!this.explicitlyClosed) this.scheduleReconnect(); });
  }

  private createSocket(jwt: string): void {
    if (!this.url) return;
    const protocols = jwt ? [jwt] : [];
    const socket = new this.WSCtor(this.url, protocols);
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.attempts = 0;
      this.emitStatus({ kind: 'open' });
    });

    socket.addEventListener('message', (event: MessageEvent) => {
      this.handleRaw(typeof event.data === 'string' ? event.data : String(event.data));
    });

    socket.addEventListener('close', (event: CloseEvent) => {
      this.socket = null;
      this.emitStatus({ kind: 'closed', code: event.code, reason: event.reason });
      if (this.explicitlyClosed) {
        return;
      }
      this.scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      // close handler will fire next; treat error as informational
    });
  }

  private scheduleReconnect(): void {
    this.attempts += 1;
    if (this.attempts > this.maxAttempts) {
      this.emitStatus({
        kind: 'permanent_failure',
        reason: `exceeded ${this.maxAttempts} reconnect attempts`,
      });
      for (const [, pending] of this.pending) {
        pending.reject(new Error('permanent_failure'));
      }
      this.pending.clear();
      return;
    }
    const exp = this.baseDelayMs * 2 ** (this.attempts - 1);
    const delay = Math.min(exp, this.maxDelayMs);
    this.emitStatus({ kind: 'reconnecting', attempt: this.attempts, delay_ms: delay });
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
  }

  private handleRaw(text: string): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return;
    }
    if (!parsed || typeof parsed !== 'object') {
      return;
    }
    const msg = parsed as RpcRequest & RpcResponse;
    if (msg.jsonrpc !== JSONRPC_VERSION) {
      return;
    }
    // response: id present, no method, has result or error
    if (msg.method === undefined && msg.id !== undefined && msg.id !== null) {
      const key = String(msg.id);
      const pending = this.pending.get(key);
      if (pending) {
        this.pending.delete(key);
        pending.resolve(msg as unknown as RpcResponse);
      }
      return;
    }
    // notification: method present, no id
    if (typeof msg.method === 'string' && (msg.id === undefined || msg.id === null)) {
      if (this.notifications) {
        this.notifications(msg.method, msg.params);
      }
      return;
    }
    // request: method + id
    if (typeof msg.method === 'string' && msg.id !== undefined) {
      this.dispatchIncoming(msg).catch(() => {
        // dispatcher errors already encoded into a response inside dispatchIncoming
      });
    }
  }

  private async dispatchIncoming(req: RpcRequest): Promise<void> {
    if (!this.incoming) {
      this.respondError(
        req.id ?? null,
        ERROR_CODES.METHOD_NOT_FOUND,
        `no incoming handler for ${req.method}`,
      );
      return;
    }
    try {
      const result = await this.incoming(req);
      if (result && this.socket && this.socket.readyState === this.WSCtor.OPEN) {
        this.socket.send(JSON.stringify(result));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'internal error';
      this.respondError(req.id ?? null, ERROR_CODES.INTERNAL_ERROR, message);
    }
  }

  private respondError(id: RpcId, code: number, message: string): void {
    if (!this.socket || this.socket.readyState !== this.WSCtor.OPEN) {
      return;
    }
    const env: RpcErrorResponse = {
      jsonrpc: JSONRPC_VERSION,
      id,
      error: { code, message },
    };
    this.socket.send(JSON.stringify(env));
  }

  private emitStatus(status: WsStatus): void {
    if (this.statusListener) {
      this.statusListener(status);
    }
  }
}
